import React, { useState } from 'react';
import {
  FiArrowRight, FiArrowLeft, FiCheck, FiTerminal,
  FiUser, FiMail, FiBriefcase, FiMessageSquare,
} from 'react-icons/fi';

/* ── Service-specific onboarding questions ─────────────── */
const SERVICE_STEPS = {
  'ai-automation': {
    label: 'AI & Automation',
    steps: [
      {
        title: 'Current Workflow',
        subtitle: 'Help us understand what you\'re working with today.',
        fields: [
          { name: 'currentTools', type: 'select', label: 'What automation tools are you using today?',
            options: ['None / manual processes', 'Zapier / Make', 'Custom scripts', 'UiPath / Power Automate', 'Other'] },
          { name: 'manualProcesses', type: 'textarea', label: 'Which manual processes are costing you the most time?', placeholder: 'e.g. Data entry from emails, report generation, lead qualification…' },
        ],
      },
      {
        title: 'AI Goals',
        subtitle: 'What does success look like?',
        fields: [
          { name: 'aiGoal', type: 'select', label: 'Primary goal for AI implementation',
            options: ['Automate repetitive tasks', 'Build internal copilot / assistant', 'Document intelligence / extraction', 'Customer-facing AI (chatbot, voice)', 'Predictive analytics / ML', 'Not sure — need guidance'] },
          { name: 'dataReadiness', type: 'select', label: 'How would you rate your data readiness?',
            options: ['Data is clean & centralized', 'Data exists but scattered', 'Mostly unstructured documents', 'Starting from scratch'] },
        ],
      },
      {
        title: 'Scale & Timeline',
        subtitle: 'Help us size the engagement.',
        fields: [
          { name: 'volume', type: 'select', label: 'Expected volume / scale',
            options: ['< 1,000 tasks/month', '1K – 10K tasks/month', '10K – 100K tasks/month', '100K+ tasks/month'] },
          { name: 'timeline', type: 'select', label: 'Ideal timeline to production',
            options: ['ASAP (< 4 weeks)', '1–2 months', '3–6 months', 'Exploring / no rush'] },
        ],
      },
    ],
  },

  'software-engineering': {
    label: 'Software Engineering',
    steps: [
      {
        title: 'Project Scope',
        subtitle: 'Tell us about what you\'re building.',
        fields: [
          { name: 'projectType', type: 'select', label: 'What type of project is this?',
            options: ['New product / MVP build', 'Legacy modernization', 'Feature development', 'Performance / scaling', 'DevOps / infrastructure', 'Staff augmentation'] },
          { name: 'techStack', type: 'textarea', label: 'Current tech stack (if any)', placeholder: 'e.g. React, Node.js, PostgreSQL, AWS…' },
        ],
      },
      {
        title: 'Team & Process',
        subtitle: 'How does your engineering team work?',
        fields: [
          { name: 'teamSize', type: 'select', label: 'Current engineering team size',
            options: ['Solo founder / no engineers', '1–5 engineers', '6–20 engineers', '20+ engineers'] },
          { name: 'methodology', type: 'select', label: 'Development methodology',
            options: ['Agile / Scrum', 'Kanban', 'No formal process', 'Other'] },
        ],
      },
      {
        title: 'Requirements',
        subtitle: 'Help us plan the engagement.',
        fields: [
          { name: 'engagementType', type: 'select', label: 'Preferred engagement model',
            options: ['Embedded team (in your workflow)', 'Project-based delivery', 'Staff augmentation (individual engineers)', 'Advisory / architecture review'] },
          { name: 'timeline', type: 'select', label: 'Target launch / delivery date',
            options: ['< 4 weeks', '1–2 months', '3–6 months', '6+ months'] },
        ],
      },
    ],
  },

  'data-analytics': {
    label: 'Data & Analytics',
    steps: [
      {
        title: 'Data Landscape',
        subtitle: 'Where does your data live today?',
        fields: [
          { name: 'dataSources', type: 'select', label: 'Primary data sources',
            options: ['SQL databases', 'SaaS tools (Salesforce, HubSpot, etc.)', 'Spreadsheets / CSVs', 'APIs / streaming data', 'Unstructured docs / files', 'Multiple of the above'] },
          { name: 'currentWarehouse', type: 'select', label: 'Current data warehouse',
            options: ['None / considering options', 'Snowflake', 'BigQuery', 'Redshift', 'PostgreSQL / other DB', 'Other'] },
        ],
      },
      {
        title: 'Analytics Goals',
        subtitle: 'What insights are you after?',
        fields: [
          { name: 'analyticsGoal', type: 'select', label: 'Primary analytics objective',
            options: ['Build BI dashboards for leadership', 'Design data warehouse / pipelines', 'Predictive analytics / ML models', 'Real-time analytics & alerting', 'Data governance & quality', 'Full data platform build'] },
          { name: 'biTools', type: 'select', label: 'BI/visualization tools in use',
            options: ['None yet', 'Looker', 'Tableau', 'Metabase', 'Power BI', 'Custom / other'] },
        ],
      },
      {
        title: 'Scale & Compliance',
        subtitle: 'Sizing and regulatory considerations.',
        fields: [
          { name: 'dataVolume', type: 'select', label: 'Approximate data volume',
            options: ['< 1 GB', '1–100 GB', '100 GB – 1 TB', '1 TB+', 'Not sure'] },
          { name: 'compliance', type: 'select', label: 'Regulatory requirements',
            options: ['None specific', 'HIPAA', 'SOC 2', 'GDPR', 'PCI-DSS', 'Multiple / other'] },
        ],
      },
    ],
  },

  'crm-operations': {
    label: 'CRM & Operations',
    steps: [
      {
        title: 'Current CRM',
        subtitle: 'Tell us about your customer operations stack.',
        fields: [
          { name: 'currentCRM', type: 'select', label: 'Current CRM platform',
            options: ['No CRM yet', 'Salesforce', 'HubSpot', 'Pipedrive', 'Zoho', 'Custom / other'] },
          { name: 'crmPain', type: 'textarea', label: 'Biggest CRM / operations pain point', placeholder: 'e.g. Data is messy, reps don\'t use it, no pipeline visibility…' },
        ],
      },
      {
        title: 'Operations Goals',
        subtitle: 'What should improve?',
        fields: [
          { name: 'opsGoal', type: 'select', label: 'Primary objective',
            options: ['CRM implementation / migration', 'Sales pipeline automation', 'AI lead scoring & routing', 'Customer data unification (CDP)', 'Revenue operations alignment', 'Support / success automation'] },
          { name: 'customerVolume', type: 'select', label: 'Approximate customer / lead volume',
            options: ['< 500 contacts', '500 – 5,000', '5K – 50K', '50K+'] },
        ],
      },
      {
        title: 'Integrations',
        subtitle: 'What else needs to connect?',
        fields: [
          { name: 'integrations', type: 'textarea', label: 'Key tools that need to integrate', placeholder: 'e.g. Stripe, Intercom, Mailchimp, Slack, your product DB…' },
          { name: 'timeline', type: 'select', label: 'Ideal go-live timeline',
            options: ['ASAP (< 4 weeks)', '1–2 months', '3–6 months', 'Exploring / no rush'] },
        ],
      },
    ],
  },
};

/* fallback for unknown service slugs */
const DEFAULT_STEPS = {
  label: 'General',
  steps: [
    {
      title: 'Your Needs',
      subtitle: 'Give us a high-level overview.',
      fields: [
        { name: 'goal', type: 'textarea', label: 'What challenge are you trying to solve?', placeholder: 'Describe your project or pain point…' },
        { name: 'timeline', type: 'select', label: 'Ideal timeline',
          options: ['ASAP', '1–3 months', '3–6 months', 'Exploring'] },
      ],
    },
  ],
};

/**
 * ConsultationForm — multi-step onboarding form per service type.
 * Props: serviceSlug, color
 */
export default function ConsultationForm({ serviceSlug, color }) {
  const config = SERVICE_STEPS[serviceSlug] || DEFAULT_STEPS;
  const totalSteps = config.steps.length + 1; // onboarding steps + contact step
  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState({
    name: '', email: '', company: '', message: '',
  });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const next = () => setStep((s) => Math.min(s + 1, totalSteps - 1));
  const prev = () => setStep((s) => Math.max(s - 1, 0));

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
  };

  const accentBorder = `${color}33`;

  if (submitted) {
    return (
      <div className="cf-container" style={{ '--cf-color': color }}>
        <div className="cf-success">
          <div className="cf-success-icon" style={{ color }}>
            <FiCheck size={32} />
          </div>
          <h3>You're all set!</h3>
          <p>
            We've received your <strong>{config.label}</strong> consultation request.
            A specialist will reach out within 24 hours.
          </p>
        </div>
      </div>
    );
  }

  const isContactStep = step === totalSteps - 1;
  const currentOnboardingStep = !isContactStep ? config.steps[step] : null;

  return (
    <div className="cf-container" style={{ '--cf-color': color }}>
      {/* Progress */}
      <div className="cf-progress">
        {[...Array(totalSteps)].map((_, i) => (
          <div key={i} className={`cf-progress-step ${i < step ? 'done' : ''} ${i === step ? 'active' : ''}`}>
            <div className="cf-progress-dot" style={i <= step ? { background: color, borderColor: color } : {}}>
              {i < step ? <FiCheck size={10} /> : i + 1}
            </div>
            <span className="cf-progress-label">
              {i < config.steps.length ? config.steps[i].title : 'Contact'}
            </span>
          </div>
        ))}
        <div className="cf-progress-bar">
          <div className="cf-progress-fill" style={{ width: `${(step / (totalSteps - 1)) * 100}%`, background: color }} />
        </div>
      </div>

      <form onSubmit={isContactStep ? handleSubmit : (e) => { e.preventDefault(); next(); }}>
        {/* Onboarding Steps */}
        {currentOnboardingStep && (
          <div className="cf-step">
            <div className="cf-step-header">
              <h3>{currentOnboardingStep.title}</h3>
              <p>{currentOnboardingStep.subtitle}</p>
            </div>
            <div className="cf-fields">
              {currentOnboardingStep.fields.map((field) => (
                <div className="cf-field" key={field.name}>
                  <label>{field.label}</label>
                  {field.type === 'select' ? (
                    <select
                      name={field.name}
                      value={formData[field.name] || ''}
                      onChange={handleChange}
                      style={{ borderColor: formData[field.name] ? accentBorder : undefined }}
                    >
                      <option value="">Select…</option>
                      {field.options.map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  ) : (
                    <textarea
                      name={field.name}
                      rows={3}
                      placeholder={field.placeholder || ''}
                      value={formData[field.name] || ''}
                      onChange={handleChange}
                      style={{ borderColor: formData[field.name] ? accentBorder : undefined }}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Contact Step */}
        {isContactStep && (
          <div className="cf-step">
            <div className="cf-step-header">
              <h3>Your Details</h3>
              <p>Almost done — tell us how to reach you.</p>
            </div>
            <div className="cf-fields">
              <div className="cf-field">
                <label><FiUser size={13} /> Full Name</label>
                <input type="text" name="name" required placeholder="Jane Doe"
                  value={formData.name} onChange={handleChange} />
              </div>
              <div className="cf-field">
                <label><FiMail size={13} /> Work Email</label>
                <input type="email" name="email" required placeholder="jane@company.com"
                  value={formData.email} onChange={handleChange} />
              </div>
              <div className="cf-field">
                <label><FiBriefcase size={13} /> Company</label>
                <input type="text" name="company" required placeholder="Acme Inc."
                  value={formData.company} onChange={handleChange} />
              </div>
              <div className="cf-field">
                <label><FiMessageSquare size={13} /> Anything else? (optional)</label>
                <textarea name="message" rows={3} placeholder="Budget range, deadlines, special requirements…"
                  value={formData.message} onChange={handleChange} />
              </div>
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="cf-nav">
          {step > 0 && (
            <button type="button" className="cf-nav-back" onClick={prev}>
              <FiArrowLeft /> Back
            </button>
          )}
          <div className="cf-nav-spacer" />
          {!isContactStep ? (
            <button type="submit" className="cf-nav-next" style={{ background: color, borderColor: color }}>
              Continue <FiArrowRight />
            </button>
          ) : (
            <button type="submit" className="cf-nav-next" style={{ background: color, borderColor: color }}>
              <FiTerminal /> Submit Request
            </button>
          )}
        </div>

        <p className="cf-footer-note">
          Step {step + 1} of {totalSteps} · Free consultation · No obligation
        </p>
      </form>
    </div>
  );
}
